import { useEffect, useMemo, useState } from "react";
import { Modal, Steps, Button, message, Form, Alert, Space, Typography } from "antd";
import { InfoCircleOutlined, WarningOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  asyncGetAppGroupPlaidResults,
  asyncGetApplicationsByGroup,
  asyncgetPropertyFeeRecord,
  asyncGetPropertyUnitRent,
  asyncGetTransactionCategories,
  asyncSendLease,
} from "../../../../store/easyApp/easyapp-async";
import { clearGroupData } from "../../../../store/easyApp/easyapp-slice";

import CreateStep from "./steps/Create";
import PreviewStep from "./steps/Preview";
import SendStep from "./steps/Send";

const STEPS = [
  { title: "Create", component: CreateStep },
  { title: "Preview", component: PreviewStep },
  { title: "Send", component: SendStep },
];

const LeaseWizard = ({ open, onComplete, onCancel, appGroupID }: any) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sendingLease, setSendingLease] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sendForm] = Form.useForm();

  const dispatch = useAppDispatch();
  const {
    selectedAppGroup,
    selectedAppGroupPlaidResults,
    selectedPropertyUnitRent,
    selectedPropertyFeeRecord,
    transactionCategories,
    leasePreview,
  } = useAppSelector((state) => state.easyapp);

  const steps = useMemo(() => {
    const stepProps = {
      loading,
      selectedAppGroup,
      plaidIncome: selectedAppGroupPlaidResults,
      unitRent: selectedPropertyUnitRent,
      selectedPropertyFeeRecord,
      transactionCategories,
    };

    return STEPS.map(({ title, component: Step }) => {
      if (title === "Send") {
        return {
          title,
          content: <Step {...stepProps} form={sendForm} />,
        };
      }
      return { title, content: <Step {...stepProps} form={sendForm}/> };
    });
  }, [
    loading,
    selectedAppGroup,
    selectedAppGroupPlaidResults,
    selectedPropertyUnitRent,
    selectedPropertyFeeRecord,
    transactionCategories,
    sendForm,
  ]);  

  const init = async () => {
    if (!appGroupID) {
      setError("Application Group ID is required");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const groupResult = await dispatch(asyncGetApplicationsByGroup(appGroupID));
      const group = groupResult.payload;
      await Promise.all([
        dispatch(asyncGetAppGroupPlaidResults(appGroupID)),
        dispatch(asyncGetTransactionCategories()),
        ...(group && group.ms_property_id && group.desired_apartment
          ? [
              dispatch(
                asyncGetPropertyUnitRent({
                  id: group.ms_property_id,
                  unitId: group.desired_apartment,
                })
              ),
              dispatch(asyncgetPropertyFeeRecord(group.ms_property_id)),
            ]
          : []),
      ]);
    } catch (err) {
      setError("Failed to load application data");
      message.error("Failed to load application data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setCurrentStep(0);
    setError(null);
    dispatch(clearGroupData());
    onCancel();
  };

  const finish = async () => {
    try {
      setSendingLease(true);
      const values = await sendForm.validateFields();

      const leaseholders = selectedAppGroup?.leaseDraft?.leaseholders || [];

      const payload = {
        leaseholders,
        signingMode: values.signingMode,
        propertyId: selectedAppGroup?.ms_property_id,
        propertyName: selectedAppGroup?.property_name,
        desiredUnitNumber: selectedAppGroup?.desired_unit_number,
        appGroupID,
      };

      const formData = new FormData();
      formData.append("leaseFile", leasePreview, "lease.pdf");
      formData.append("payload", JSON.stringify(payload));

      const res = await dispatch(asyncSendLease(formData));

      if (res.payload.success) {
        message.success("Lease sent for signing!");
        await onComplete();
      } else {
        message.error("Failed to sent lease for signing!");
      }
    } catch (err) {
      message.error("Failed to complete lease creation. Please try again.");
    } finally {
      setSendingLease(false);
    }
  };

  useEffect(() => {
    if (open && appGroupID) init();
  }, [open, appGroupID]);

  useEffect(() => {
    return () => {
      dispatch(clearGroupData());
    };
  }, [dispatch]);

  const footer = (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
      {currentStep > 0 && (
        <Button onClick={() => setCurrentStep((s) => s - 1)} disabled={loading}>
          Back
        </Button>
      )}
      {currentStep < steps.length - 1 && (
        <Button
          type="primary"
          onClick={() => setCurrentStep((s) => s + 1)}
          loading={loading}
          disabled={
            loading ||
            (currentStep === 0 &&
              (
                !selectedAppGroup?.leaseDraft || 
                Object.keys(selectedAppGroup.leaseDraft).length === 0
              )
            )
          }          
        >
          Next
        </Button>
      )}
      {currentStep === steps.length - 1 && (
        <Button
          type="primary"
          onClick={finish}
          loading={sendingLease}
          disabled={sendingLease}
        >
          Finish & Send
        </Button>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title="Lease Wizard"
      onCancel={resetAndClose}
      width={1200}
      destroyOnClose
      maskClosable={false}
      footer={footer}
    >
      <Steps
        current={currentStep}
        items={steps.map(({ title }) => ({
          title,
          status: error && currentStep === 0 ? "error" : undefined,
        }))}
        style={{ marginBottom: 24 }}
      />

      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 24,
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 6,
            color: "#a8071a",
          }}
        >
          {error}
        </div>
      )}

      {/* Draft Info Banner */}
      {selectedAppGroup?.leaseDraft && (
        <div style={{ marginBottom: 16 }}>
          {/* Unit mismatch warning */}
          {selectedAppGroup.leaseDraft.savedUnit && 
           selectedAppGroup.leaseDraft.savedUnit !== selectedAppGroup.desired_unit_number && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 8 }}
              message={
                <Space>
                  <Typography.Text strong>Unit Number Changed</Typography.Text>
                </Space>
              }
              description={
                <Typography.Text>
                  This draft was created for unit <strong>{selectedAppGroup.leaseDraft.savedUnit}</strong>, 
                  but the application now shows unit <strong>{selectedAppGroup.desired_unit_number}</strong>. 
                  The lease will use the current unit number. Please review and save the draft again to update.
                </Typography.Text>
              }
            />
          )}
          
          {/* Draft saved timestamp */}
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message={
              <Space>
                <Typography.Text>
                  <strong>Saved Draft:</strong>{" "}
                  {selectedAppGroup.leaseDraft.savedAt 
                    ? dayjs(selectedAppGroup.leaseDraft.savedAt).format("MMM D, YYYY [at] h:mm A")
                    : "Unknown date"}
                  {selectedAppGroup.leaseDraft.owner?.name && (
                    <> by {selectedAppGroup.leaseDraft.owner.name}</>
                  )}
                </Typography.Text>
              </Space>
            }
          />
        </div>
      )}

      <div style={{ minHeight: 400 }}>{steps[currentStep]?.content}</div>
    </Modal>
  );
};

export default LeaseWizard;
